import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, ExternalLink, Save, Globe, Clock } from "lucide-react";
import { logger } from "@/lib/logger";
import SenderAgenciesCard from "@/components/SenderAgenciesCard";

export default function Settings() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({
    oauth_client_id: "",
    oauth_client_secret: "",
    ads_developer_token: "",
    ads_login_customer_id: "",
    redirect_uri: "",
  });
  const [busy, setBusy] = useState(false);

  // Master hourly rate state
  const [rate, setRate] = useState(0);
  const [rateInput, setRateInput] = useState("");
  const [rateCascade, setRateCascade] = useState(true);
  const [rateBusy, setRateBusy] = useState(false);

  const loadAppSettings = async () => {
    try {
      const { data } = await api.get("/app-settings");
      const v = Number(data?.default_hourly_rate || 0);
      setRate(v); setRateInput(v ? String(v) : "");
    } catch (err) {
      // Settings endpoint failure is non-fatal — leave rate at 0 / empty
      // input. Log for diagnostics but don't toast (the user might land
      // on this page before /app-settings is seeded).
      logger.warn("loadAppSettings: /app-settings unavailable", err);
    }
  };

  const saveRate = async (e) => {
    e.preventDefault();
    const v = Number(rateInput);
    if (!Number.isFinite(v) || v < 0) {
      toast.error("Enter a valid hourly rate"); return;
    }
    setRateBusy(true);
    try {
      const { data } = await api.put("/app-settings", {
        default_hourly_rate: v, cascade: rateCascade,
      });
      setRate(Number(data?.default_hourly_rate || 0));
      const c = data?.cascade;
      if (rateCascade && c) {
        toast.success(
          `Default rate $${v.toFixed(2)} saved · updated ${c.clients_updated} client(s) and ${c.business_units_updated} property budget(s)`,
        );
      } else {
        toast.success(`Default rate $${v.toFixed(2)} saved`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setRateBusy(false); }
  };

  const load = async () => {
    const { data } = await api.get("/integrations/google/settings");
    setS(data);
    setForm((f) => ({
      ...f,
      oauth_client_id: data.oauth_client_id || "",
      ads_login_customer_id: data.ads_login_customer_id || "",
      redirect_uri: data.redirect_uri ||
        `${window.location.origin.replace(/^https?:\/\/[^/]+/, process.env.REACT_APP_BACKEND_URL)}/api/google/oauth/callback`,
    }));
  };

  // Mount-only loader. `load`/`loadAppSettings` are defined in this same
  // component but only need to fire once on entry — subsequent reloads are
  // user-triggered via the save handlers, which already call them directly.
  useEffect(() => { load(); loadAppSettings(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      // Only send secret/token if the admin actually typed something new
      if (!body.oauth_client_secret) delete body.oauth_client_secret;
      if (!body.ads_developer_token) delete body.ads_developer_token;
      await api.put("/integrations/google/settings", body);
      toast.success("Settings saved");
      setForm((f) => ({ ...f, oauth_client_secret: "", ads_developer_token: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <div className="label-overline">Configuration</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Integrations &amp; Settings</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Store Google OAuth credentials so the platform can pull live data from Google Search Console, Google Analytics (GA4) and Google Ads.
          Secrets are AES-encrypted before being saved.
        </p>
      </div>

      <Card className="rounded-sm border-zinc-200" data-testid="master-rate-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Default hourly rate</div>
                <div className="text-xs text-zinc-500">
                  Pre-fills every &quot;hrs&quot; budget row on new clients + properties.
                </div>
              </div>
            </div>
            <Badge className="rounded-sm bg-zinc-100 text-zinc-700"
              data-testid="current-rate-badge">
              Current: ${Number(rate || 0).toFixed(2)} / hr
            </Badge>
          </div>          <form onSubmit={saveRate}
            className="grid md:grid-cols-[200px_1fr_auto] gap-3 items-end pt-2 border-t border-zinc-100">
            <div className="space-y-1.5">
              <Label className="label-overline">New rate ($ / hr)</Label>
              <Input type="number" step="0.01" min="0"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                className="rounded-sm font-mono text-xs"
                placeholder="175.00"
                data-testid="default-rate-input" />
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-700"
              data-testid="cascade-rate-row">
              <input type="checkbox" checked={rateCascade}
                onChange={(e) => setRateCascade(e.target.checked)}
                data-testid="cascade-rate-toggle" />
              Also update every existing &quot;hrs&quot; budget row across all clients +
              properties to this new rate.
            </label>
            <Button type="submit" disabled={rateBusy}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="save-default-rate">
              <Save className="h-4 w-4 mr-2" /> {rateBusy ? "Saving…" : "Save rate"}
            </Button>
          </form>
          <p className="text-[11px] text-zinc-500">
            New &quot;hrs&quot; budget rows added on any client or property will use this rate as
            their default. Existing rows are only changed when you tick the cascade option.
          </p>
        </CardContent>
      </Card>

      <SenderAgenciesCard />

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Google Marketing APIs</div>
                <div className="text-xs text-zinc-500">GA4 · Search Console · Google Ads</div>
              </div>
            </div>
            <Badge className={`rounded-sm ${s?.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
              data-testid="google-status-badge">
              {s?.configured ? <ShieldCheck className="h-3 w-3 mr-1" /> : <KeyRound className="h-3 w-3 mr-1" />}
              {s?.configured ? "Credentials saved" : "Awaiting credentials"}
            </Badge>
          </div>

          <form onSubmit={save} className="space-y-5 pt-2 border-t border-zinc-100">
            <div className="grid md:grid-cols-2 gap-4 pt-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="label-overline">Authorised redirect URI</Label>
                <Input className="rounded-sm font-mono text-xs"
                  value={form.redirect_uri}
                  onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
                  data-testid="google-redirect-input" />
                <p className="text-xs text-zinc-500">
                  Paste this exact URL into your Google Cloud Console OAuth client&apos;s &quot;Authorised redirect URIs&quot; field.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">OAuth Client ID</Label>
                <Input className="rounded-sm font-mono text-xs"
                  placeholder="1234567890-abcdef.apps.googleusercontent.com"
                  value={form.oauth_client_id}
                  onChange={(e) => setForm({ ...form, oauth_client_id: e.target.value })}
                  data-testid="google-client-id-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">
                  OAuth Client Secret {s?.has_client_secret && <span className="text-emerald-600 normal-case font-normal">(saved · paste new to overwrite)</span>}
                </Label>
                <Input type="password" className="rounded-sm font-mono text-xs"
                  placeholder="GOCSPX-xxxxxxxxxxxxxxxx"
                  value={form.oauth_client_secret}
                  onChange={(e) => setForm({ ...form, oauth_client_secret: e.target.value })}
                  data-testid="google-client-secret-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">
                  Google Ads Developer Token {s?.has_ads_developer_token && <span className="text-emerald-600 normal-case font-normal">(saved)</span>}
                </Label>
                <Input type="password" className="rounded-sm font-mono text-xs"
                  placeholder="22-character token from Google Ads API Center"
                  value={form.ads_developer_token}
                  onChange={(e) => setForm({ ...form, ads_developer_token: e.target.value })}
                  data-testid="google-ads-token-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Google Ads Manager Customer ID</Label>
                <Input className="rounded-sm font-mono text-xs"
                  placeholder="1234567890 (no dashes)"
                  value={form.ads_login_customer_id}
                  onChange={(e) => setForm({ ...form, ads_login_customer_id: e.target.value })}
                  data-testid="google-ads-customer-id-input" />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={busy}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="settings-save-button">
                <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save credentials"}
              </Button>
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer"
                className="text-xs text-[#002FA7] inline-flex items-center gap-1 hover:underline">
                Open Google Cloud Console <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200 bg-zinc-50/60">
        <CardContent className="p-6 space-y-3">
          <div className="label-overline">Quick setup checklist</div>
          <ol className="list-decimal pl-5 text-sm text-zinc-700 space-y-2">
            <li>Create a project at <a className="text-[#002FA7] underline" target="_blank" rel="noreferrer" href="https://console.cloud.google.com/">console.cloud.google.com</a> and enable: Analytics Data API, Analytics Admin API, Search Console API, Google Ads API.</li>
            <li>Configure the OAuth consent screen as <em>External</em>, add scopes for <code className="text-xs bg-zinc-100 px-1">analytics.readonly</code>, <code className="text-xs bg-zinc-100 px-1">webmasters.readonly</code>, <code className="text-xs bg-zinc-100 px-1">adwords</code>, plus <code className="text-xs bg-zinc-100 px-1">openid email profile</code>. Add yourself as a test user.</li>
            <li>Create an OAuth client of type <em>Web application</em>, paste the redirect URI above. Copy the Client ID + Client Secret into the form above.</li>
            <li>For Google Ads, apply for a Developer Token in your Ads Manager → Tools → API Center (Basic Access is fine). Copy the token + Manager Customer ID.</li>
            <li>Click <strong>Save credentials</strong>. Once OAuth is wired up, a &quot;Connect Google account&quot; button will appear on the API Connections page.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
